import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Plus, FlaskConical, CreditCard, Landmark, Sparkles, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import DebtModal from "@/components/DebtModal";
import PayoffPlanCard from "@/components/debts/PayoffPlanCard";
import ProgressOverview from "@/components/debts/ProgressOverview";
import MonthlyPayments from "@/components/debts/MonthlyPayments";
import CreditUtilization from "@/components/debts/CreditUtilization";
import DebtTable from "@/components/debts/DebtTable";
import PaymentHistorySheet from "@/components/debts/PaymentHistorySheet";
import PayoffCelebration from "@/components/debts/PayoffCelebration";
import ChargeModal from "@/components/debts/ChargeModal";
import FirstDebtCoach from "@/components/debts/FirstDebtCoach";
import AprNegotiation from "@/components/debts/AprNegotiation";
import GuidedEmpty from "@/components/GuidedEmpty";
import { toast } from "sonner";

export default function Debts() {
  const [progress, setProgress] = useState(null);
  const [modal, setModal] = useState(null); // { debt, defaultCategory }
  const [history, setHistory] = useState(null);
  const [celebrate, setCelebrate] = useState(null);
  const [charging, setCharging] = useState(null);
  const [showOptimize, setShowOptimize] = useState(false);
  const [insights, setInsights] = useState(null);
  const [showUtil, setShowUtil] = useState(false);
  const [params, setParams] = useSearchParams();
  const [coach, setCoach] = useState(params.get("coach") === "1");
  const closeCoach = () => { setCoach(false); if (params.get("coach")) { params.delete("coach"); setParams(params, { replace: true }); } };

  const load = () => {
    api.get("/debts/progress").then(r => setProgress(r.data));
    api.get("/insights").then(r => setInsights(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.name}"? Its linked monthly bill and payment history will be removed too.`)) return;
    await api.delete(`/debts/${d.id}`); toast.success("Account deleted"); load();
  };
  const edit = (d) => setModal({ debt: d });

  if (!progress) return <div className="h-64 rounded-2xl bg-card border border-border animate-pulse" />;
  const { totals, debts, plan, credit, comparison } = progress;
  const cards = debts.filter(d => d.category === "credit_card");
  const loans = debts.filter(d => d.category !== "credit_card");
  const hasActive = debts.some(d => !d.idle);

  return (
    <div className="space-y-6" data-testid="debts-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-indigo-400 font-semibold">Debt Progress Tracker</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Debts</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link to="/simulator" className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border text-sm font-medium hover:bg-accent transition" data-testid="debts-open-simulator">
            <FlaskConical className="h-4 w-4" /> What-if Simulator
          </Link>
          <button onClick={() => setModal({ defaultCategory: "credit_card" })} data-testid="debts-add-card-btn"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-rose-500 text-white text-sm font-semibold hover:bg-rose-600 transition">
            <CreditCard className="h-4 w-4" /> Add Credit Card
          </button>
          <button onClick={() => setModal({ defaultCategory: "loan" })} data-testid="debts-add-btn"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-indigo-500 text-white text-sm font-semibold hover:bg-indigo-600 transition">
            <Plus className="h-4 w-4" /> Add Loan
          </button>
        </div>
      </div>

      {debts.length === 0 ? (
        <GuidedEmpty
          testid="debts-empty"
          tone="teal"
          icon={Sparkles}
          title="Let's add your first card together"
          subtitle="A two-minute walkthrough that shows you where every number lives on your statement — and what it means."
          tips={[
            "We'll capture balance, APR, minimum payment and credit limit.",
            "$0-balance cards count too — they improve your utilization picture.",
            "Each card or loan gets a linked monthly bill on your calendar automatically.",
          ]}
          primary={{ label: "Start guided setup", onClick: () => setCoach(true), testid: "debts-empty-coach-btn" }}
          secondary={[
            { label: "Add credit card", onClick: () => setModal({ defaultCategory: "credit_card" }), testid: "debts-empty-add-card" },
            { label: "Add loan", onClick: () => setModal({ defaultCategory: "loan" }), testid: "debts-empty-add-loan" },
          ]}
          note="Nothing is shared until you invite someone."
        />
      ) : (
        <>
          {hasActive && (
            <div className="space-y-4">
              <button onClick={() => setShowOptimize(s => !s)} data-testid="optimize-toggle"
                className="w-full flex items-center gap-3 pt-2 group">
                <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-indigo-400 whitespace-nowrap">Optimize your payoff</span>
                <span className="h-px flex-1 bg-border" />
                <span className="text-[10px] text-muted-foreground group-hover:text-foreground transition hidden sm:inline">strategy · APR</span>
                <span className="text-[10px] font-semibold text-muted-foreground group-hover:text-foreground transition">{showOptimize ? "Hide" : "Show"}</span>
                <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${showOptimize ? "rotate-180" : ""}`} />
              </button>
              {showOptimize && (
                <div className="space-y-4" data-testid="optimize-panel">
                  <PayoffPlanCard plan={plan} comparison={comparison} onSaved={load} />
                  <AprNegotiation debts={debts} onSaved={load} />
                </div>
              )}
            </div>
          )}

          {(hasActive || credit.cards.length > 0) && (
            <>
              <SectionHeader label="Where you stand" hint="Your progress at a glance" />
              {hasActive ? (
                <>
                  <ProgressOverview
                    totals={totals}
                    savings={progress.savings_vs_minimums}
                    streak={insights?.payment_streak_months ?? 0}
                    utilization={credit.cards.length ? credit.total_utilization : null}
                    utilGoal={credit.goal_pct}
                    onUtilClick={credit.cards.length ? () => setShowUtil(s => !s) : undefined}
                    utilOpen={showUtil}
                  />
                  {credit.cards.length > 0 && showUtil && <CreditUtilization credit={credit} plan={plan} onChanged={load} detailsOnly />}
                </>
              ) : (
                credit.cards.length > 0 && <CreditUtilization credit={credit} plan={plan} onChanged={load} />
              )}
            </>
          )}

          <SectionHeader label="Due this month" hint="Log payments as you make them" />
          <MonthlyPayments debts={debts} plan={plan} onPaid={load} onPaidOff={setCelebrate} />

          <SectionHeader label="Your accounts" hint="Every card and loan you track" />
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-semibold text-rose-400"><CreditCard className="h-3.5 w-3.5" /> Revolving credit</div>
            <DebtTable testid="cards-table" title="Credit cards" cards
              subtitle="Revolving accounts · zero-balance cards still count toward your available credit"
              debts={cards} emptyText='No credit cards yet. Use "Add Credit Card" — even a $0 balance card improves your utilization picture.'
              onEdit={edit} onDelete={remove} onHistory={setHistory} onCharge={setCharging} />
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-semibold text-indigo-400 pt-2"><Landmark className="h-3.5 w-3.5" /> Installment debt</div>
            <DebtTable testid="loans-table" title="Loans & other debts"
              subtitle="Student, auto, personal, mortgage, medical — each keeps a linked monthly bill on your calendar"
              debts={loans} emptyText='No loans yet. Use "Add Loan" to track one.'
              onEdit={edit} onDelete={remove} onHistory={setHistory} />
          </div>
        </>
      )}

      <DebtModal open={!!modal} onClose={() => setModal(null)} debt={modal?.debt} defaultCategory={modal?.defaultCategory} onSaved={load} />
      <PaymentHistorySheet debt={history} onClose={() => setHistory(null)} />
      <PayoffCelebration payment={celebrate} onClose={() => setCelebrate(null)} />
      <ChargeModal debt={charging} onClose={() => setCharging(null)} onSaved={load} />
      <FirstDebtCoach open={coach} onClose={closeCoach} onSaved={load} />
    </div>
  );
}

function SectionHeader({ label, hint }) {
  return (
    <div className="flex items-center gap-3 pt-2" data-testid={`section-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground whitespace-nowrap">{label}</span>
      <span className="h-px flex-1 bg-border" />
      {hint && <span className="text-[10px] text-muted-foreground hidden sm:inline">{hint}</span>}
    </div>
  );
}
